import { encodeQuery } from 'Helpers/Query';
import { dockedItems, householdId, section } from 'Consts/Queries';
import history from 'State/history';
import { advisorManagedAccountPath } from 'Consts/Strings';
import { rootUri } from './sharedUris';

// Universal Uris
export const adminRoot = `${rootUri}/administrator`;

// Advisor Uris
export const advisorResourcesPath = `${adminRoot}/resources`;
export const advisorNotificationCenterPath = `${adminRoot}/notifications`;
export const advisorLandingUri = `${adminRoot}/household/landing`;
export const featuresUri = `${adminRoot}/features`;
export const accountRequestsUri = `${adminRoot}/account-requests`;
export const clientsUri = `${adminRoot}/household`;
export const workspacesUri = `${adminRoot}/workspaces`;
export const preferencesUri = `${adminRoot}/preferences`;
export const advisorHouseholdTimelineUri = id => `${clientsUri}/timeline${encodeQuery({ [householdId]: id })}`;
export const reportingUri = `${adminRoot}/reporting`;
export const partnersUri = `${adminRoot}/partners`;
export const investmentsUri = `${adminRoot}/investments`;
export const curatedContentArticlesUri = `${adminRoot}/curated-content-articles`;
export const meetingSummaryUri = `${adminRoot}/meeting-summary`;
export const analyticsUri = 'https://us-east-1.online.tableau.com/#/site/facetwealth/home';
export const planningUri = `${adminRoot}/tasks`;
export const usersUri = `${adminRoot}/user`;
export const feedUri = `${adminRoot}/feed`;
export const messagesUri = `${adminRoot}/messages`;
export const settingsUri = usersUri;
export const scheduledMessagesUri = `${adminRoot}/scheduled-messages`;
export const goalsObjectivesAndInterestsHref = id => `/administrator/household/edit/goalsObjectivesAndInterests?householdId=${id}`;
export const assumptionsAdminUri = `${adminRoot}/assumptions-admin`;
export const pageAdminUri = `${adminRoot}/page`;

export const advisorPaths = {
  partners: partnersUri,
  investments: investmentsUri,
  tasks: planningUri,
  reporting: reportingUri,
  settings: settingsUri,
  feed: feedUri,
  analytics: analyticsUri,
  preferences: preferencesUri,
  accountRequests: accountRequestsUri,
  clients: clientsUri,
  meetingSummary: meetingSummaryUri,
  messages: messagesUri,
  scheduledMessages: scheduledMessagesUri,
  curatedContentArticles: curatedContentArticlesUri,
};

const persistedQueryParams = [dockedItems];

/**
 * Helper function that creates a URLSearchParams instance excluding query params not included in persistedQueryParams
 * and includes query params from an optional params object.
 * @param params
 * @returns {URLSearchParams}
 */
const createNewSearchParams = (params = null) => {
  const currentSearchParams = new URLSearchParams(history.location.search);
  const newSearchParams = new URLSearchParams();
  currentSearchParams.forEach((value, key) => {
    if (persistedQueryParams.includes(key)) {
      newSearchParams.set(key, value);
    }
  });

  if (params) {
    Object.keys(params).forEach((key) => {
      if (params[key] != null) {
        if (typeof params[key] !== 'boolean' && typeof params[key] !== 'string' && typeof params[key] !== 'number') {
          throw new Error(`Invalid query parameter value ${params[key]} for key ${key}`);
        }
        newSearchParams.set(key, params[key]);
      }
    });
  }

  return newSearchParams;
};

/**
 * Generates a function that returns a URI string for the advisor app with a query parameters object.
 * @param route
 * @returns {function(object): string}
 */
const generateAdminUri = route => (params) => {
  const newSearchParams = createNewSearchParams(params);
  return `${adminRoot}${route}?${newSearchParams.toString()}`;
};

/**
 * Generates a function that returns a URI string for a household in the advisor app given the household ID and
 * an optional query parameters object.
 * @param route {string}
 * @returns {function(int, object): string}
 */
const generateAdminHouseholdUri = route => (id, params = null) => {
  const newSearchParams = createNewSearchParams(params);
  newSearchParams.set(householdId, id);
  return `${adminRoot}/household${route}?${newSearchParams.toString()}`;
};

/**
 * Generates a function that returns a URI string to open a modal with optional query parameters given a household ID.
 * @param modal {string}
 * @param params {object}
 * @returns {function(int): string}
 */
const generateAdminModalUri = (modal, params = null) => (id) => {
  const newSearchParams = createNewSearchParams(params);
  newSearchParams.set(householdId, id);
  newSearchParams.set('modal', modal);
  return `${history.location.pathname}?${newSearchParams.toString()}`;
};

export const advisorHouseholdUri = generateAdminHouseholdUri('/landing');
export const householdBlueprintUri = generateAdminHouseholdUri('/blueprint');
export const householdNewPrintUri = generateAdminHouseholdUri('/snapshot');
export const householdEditRecordUri = generateAdminHouseholdUri('/edit');
export const householdEditRecordPersonalInformationUri = generateAdminHouseholdUri('/edit/personalInformation');
export const householdEditRecordFamilyInformationUri = generateAdminHouseholdUri('/edit/familyInformation');
export const householdEditRecordGoalsUri = generateAdminHouseholdUri('/edit/goals');
export const householdEditRecordInvestmentAssetsUri = generateAdminHouseholdUri('/edit/investmentAssets');
export const householdEditRecordSsiAndPensionsUri = generateAdminHouseholdUri('/edit/ssiAndPensions');
export const householdEditRecordRealAssetsUri = generateAdminHouseholdUri('/edit/realAssets');
export const householdEditRecordLiabilitiesUri = generateAdminHouseholdUri('/edit/liabilities');
export const householdEditRecordEstatePlanningUri = generateAdminHouseholdUri('/edit/estatePlanning');
export const householdEditRecordFinancialProfileUri = generateAdminHouseholdUri('/edit/financialProfile');
export const householdEditRecordOtherAdvisorsUri = generateAdminHouseholdUri('/edit/otherAdvisors');
export const householdEditRecordExpenseUri = generateAdminHouseholdUri('/edit/expense');
export const householdEditRecordAdditionalNotesUri = generateAdminHouseholdUri('/edit/additionalNotes');
export const householdEditRecordInsuranceUri = generateAdminHouseholdUri('/edit/insurance');
export const householdEditRecordIncomeDetailsUri = generateAdminHouseholdUri('/edit/incomeDetails');
export const householdPlansUri = generateAdminHouseholdUri('/plan');
export const householdPlanStartUri = generateAdminHouseholdUri('/plan/start');
export const householdPlanDataUri = generateAdminHouseholdUri('/plan/data');
export const householdPlanSummaryUri = generateAdminHouseholdUri('/plan/summary');
export const householdTimelineUri = generateAdminHouseholdUri('/timeline');
export const householdSnapshotUri = generateAdminHouseholdUri('/snapshot');
export const householdSecureFilesUri = generateAdminHouseholdUri('/secureFiles');
export const householdPayStubsUri = householdID => generateAdminHouseholdUri('/secureFiles')(householdID, { folderName: 'Pay Stubs' });
export const taskBoardUri = generateAdminUri('/tasks');
export const householdAddNoteModal = generateAdminModalUri('advisorNote');
export const householdActionItemModal = generateAdminModalUri('todo', { assignedTo: 'household' });
export const advisorHouseholdSchedulerUri = generateAdminHouseholdUri('/scheduler');
export const accountSupportUri = (hhId, linkUrl) => `${adminRoot}/household/account-support${encodeQuery({
  [householdId]: hhId,
  link: linkUrl,
})}`;
export const advisorManagedAccountUri = (hhId, link, tab = null) => {
  // query parameters to be included
  const queryParams = {
    [householdId]: hhId,
    link,
  };
  // optional query parameters
  if (tab !== null) queryParams.tab = tab;
  return `${advisorManagedAccountPath}${encodeQuery(queryParams)}`;
};
export const householdHoldings = generateAdminHouseholdUri('/snapshot');
export const advisorMessagesUri = generateAdminUri('/messages');

export const advisorWhatIfPageUri = (whatIfSelfLink, hid) => {
  if (whatIfSelfLink === undefined) {
    return pageAdminUri;
  }
  return `${pageAdminUri}/what-if${encodeQuery({
    pageSelfLink: whatIfSelfLink,
    householdId: hid,
  })}`;
};

export const genericPageURI = (genericPageSelfLink, args = {}) => {
  const { pagePath, pageType } = args;
  if (genericPageSelfLink === undefined) {
    return pageAdminUri;
  }
  const pagePathString = pagePath ? `/${pagePath}` : '';
  if (pageType) {
    return `${pageAdminUri}${pagePathString}${encodeQuery({
      pageType,
      pageSelfLink: genericPageSelfLink,
    })}`;
  }
  return `${pageAdminUri}${pagePathString}${encodeQuery({
    pageSelfLink: genericPageSelfLink,
  })}`;
};

export const advisorHouseholdScheduledMessagesUri = scheduledMessagesSelfLink => {
  if (scheduledMessagesSelfLink === undefined) {
    return scheduledMessagesUri;
  }
  return `${scheduledMessagesUri}${encodeQuery({
    scheduledMessagesSelfLink,
  })}`;
};

export const householdInvestmentAssetsUri = (id, sectionToExpand = '') => `${clientsUri}/edit/investmentAssets${encodeQuery({
  [householdId]: id,
  [section]: sectionToExpand,
})}`;

export const advisorQuestionnairePath = `${clientsUri}/edit`;

export const householdFamilyInfoUri = id => `${clientsUri}/edit/familyInformation${encodeQuery({ [householdId]: id })}`;

// Old stuff below
export const advisorPlanListUri = `${advisorHouseholdUri}/plan`;
export const advisorPlanStartUri = `${advisorPlanListUri}/start`;
